import { Component, computed, inject, signal, TemplateRef, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Button, Dropdown } from '@ntv360/component-pantry';
import {
    StatsCardComponent,
    StatsData,
    TableComponent,
    TableToolbarButton,
    TableToolbarComponent,
} from '@shared';

import { ExportService } from '@core';
import { Hosts } from '@features/hosts/interface';
import { SubNavbarService } from '@layouts/main-layout/components';
import { SVG_ICONS, TIMEZONE } from '@shared/constants';
import { SCREENS_STATS } from './constants/screens-stats.constants';
import { SCREENS_COLUMNS, SCREENS_DATA } from './constants/screens-tables.constants';
import { UIScreens } from './interfaces';

@Component({
    selector: 'app-screens',
    standalone: true,
    imports: [StatsCardComponent, TableToolbarComponent, TableComponent, Button],
    templateUrl: './screens.component.html',
    styleUrl: './screens.component.scss',
})
export class ScreensComponent {
    /**
     * Injected export service for CSV export.
     */
    private exportService = inject(ExportService);

    /**
     * Injected router for navigation.
     */
    private router = inject(Router);

    /**
     * Sub-Navbar service used to programmatically project content into its view
     */
    private subNavbarService = inject(SubNavbarService);

    /**
     * Content reference to project into the sub-navbar component
     */
    protected subNavBarContent = viewChild<TemplateRef<any>>('subNavBarContent');

    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Path to the SVG sprite file.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * List of stats cards to display.
     */
    public statsData = computed(() => SCREENS_STATS);

    /**
     * Table columns definition.
     */
    public screensColumns = SCREENS_COLUMNS;

    /**
     * Table row data.
     */
    public screensData = SCREENS_DATA;

    /**
     * Signal to track selected rows in the table.
     */
    public selectedRows = signal<UIScreens[]>([]);

    /**
     * Dropdown config for timezone filter.
     */
    public timezoneConfig = {
        options: TIMEZONE,
        placeholder: 'Timezone',
        fullWidth: false,
    };

    /**
     * Computed list of toolbar buttons, reacting to selection changes.
     */
    public toolbarButtons = computed<TableToolbarButton[]>(() => {
        const selectionCount = this.selectedRows().length;
        return [
            {
                buttonName: 'Bulk Delete',
                icon: 'delete',
                variant: selectionCount > 2 ? 'danger' : 'secondary',
            },
            {
                buttonName: 'Export',
                icon: 'export',
                variant: 'accent',
            },
        ];
    });

    /**
     * Project the content into the sub-navbar component after view initializes
     */
    ngAfterViewInit(): void {
        if (this.subNavBarContent()) {
            this.subNavbarService.setContent(this.subNavBarContent()!);
        }
    }

    /**
     * Handles toolbar action click events.
     * This returns the action name, Example Clicked the Delete Button will return 'Bulk Delete'
     *
     * @param action - The action name
     */
    public onToolbarAction(action: string): void {
        if (action === 'Export') {
            const dataToExport =
                this.selectedRows().length > 0 ? this.selectedRows() : this.screensData;

            this.exportService.exportToExcel(dataToExport, 'Screens', this.screensColumns);
        }
    }

    /**
     * Handles action click events from the table.
     *Triggers when the user clicks on an action button in the table toolbar.
     *
     * @param event - The action event containing action name and row data
     */
    public onActionClick(event: { action: string; row: any }): void {
        console.log('Action clicked:', event.action, 'Row data:', event.row);
    }

    /**
     * Handles cell click events from the table (for columns with hasRedirect: true).
     *Triggers when the user clicks on a cell that has a redirect link.

     * @param event - The cell event containing field name, value, and row data
     */
    public onCellClick(event: { field: string; value: any; row: any }): void {
        console.log('Cell clicked:', event.field, 'Value:', event.value, 'Row data:', event.row);

        // Navigate to single screen page
        if (event.row.id) {
            const url = `/screens/${event.row.id}`;
            window.open(url, '_blank');
        }
    }

    /**
     * Handles selection change events from the table.
     *Triggers when the user selects or deselects a row.
     *
     * @param selectedRows - The array of currently selected rows
     */
    public onSelectionChange(selectedRows: UIScreens[]): void {
        console.log(selectedRows, 'selected rows');
        this.selectedRows.set(selectedRows);
    }

    /**
     * Handles the click event for the "Create Screen" button.
     */
    public onCreateScreen(): void {
        console.log('Create Screen button clicked');
    }
}
